import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { formatTimeStamp } from '../utils/dates'
import { currencyFormat, formatFloat } from '../utils/numbers'
import { formatPhoneNumber } from '../utils/phone'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { CacheFactory } from './CacheFactory'
import { Channel } from './Channel'
import { Contract } from './Contract'
import { Edition } from './Edition'
import { Image } from './Image'
import { Industry } from './Industry'
import { Mappable } from './Mappable'
import { Option } from './Option'
import { Order } from './Order'
import { Person } from './Person'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { Sortable } from './Sortable'
import { Taxcode } from './Taxcode'
import { Type } from './Type'

type UserConstructorArgs = Omit<ConstructorArgs<() => User>, 'location' | 'allow_sales_tax'> & {
    location: boolean | null
    allow_salestax: boolean | null
}

export class User extends Person implements Mappable, Sortable {
    readonly recordType = 'user'

    readonly iMappable = true
    readonly iSortable = true

    static PASSWORD_SIZE = 4

    static PERM_ROLE_CLIENT = 0 // perm role - client
    static PERM_ROLE_CONTRACTOR = 1 // perm role - contractor
    static PERM_ROLE_EMPLOYEE = 2 // perm role - employee
    static PERM_ROLE_ADMIN = 3 // perm role - admin
    static PERM_ROLE_OWNER = 4 // perm role - owner
    static PERM_ROLE_PARTNER = 5 // perm role - partner

    static PERM_LEVEL_VIEW = 1 // perm level - view
    static PERM_LEVEL_EDIT = 2 // perm level - edit
    static PERM_LEVEL_DELETE = 3 // perm level - delete
    static PERM_LEVEL_OWNER = 4 // perm level - owner

    url?: string
    username?: string
    bio?: string
    type?: string
    role?: string
    source?: string

    login?: string
    roleSubtitle?: string
    roleBadge?: number

    sms?: string
    main?: string
    size?: string
    goal?: string
    income?: string
    volume?: string
    team?: boolean
    password?: string

    marketingReferrer?: string
    instagram?: string
    ship?: number
    shipType?: string
    shipPricing?: string
    parcel?: string
    carrier?: string

    private _pickupDuration?: number // Integer

    signupDate?: moment.Moment
    firstSignupDate?: moment.Moment
    blockPaymentsDate?: moment.Moment

    lastSyncDate?: moment.Moment
    lastWidgetDate?: moment.Moment
    lockedAlertsDate?: moment.Moment

    travel?: string
    channel?: string
    reminder?: string
    invoiceReminder?: string
    bookingStatus?: string
    confirmPayment?: string
    bookingFlexStart?: string
    bookingAssignment?: string
    instantPayout?: boolean
    firstMPCDate?: moment.Moment
    silenceTransactions?: string
    doubleNotify?: boolean

    /* service provider fields */
    uri?: string
    key?: string
    activatedDate?: moment.Moment
    verifiedDate?: moment.Moment

    /* default descriptions */
    orderPolicy?: string
    invoicePolicy?: string
    receiptPolicy?: string
    bookingPolicy?: string
    estimatePolicy?: string

    /* scheduling / calendar settings */
    timezone?: string
    schedule?: number // Integer
    private _capacity?: number // Integer
    private _reservationCapacity?: number // Integer
    gratuity?: number
    requestGratuity?: string
    minimumGratuity?: number
    gratuityOptions?: number[] // List<Integer>
    availableRange?: string
    vendorLocation?: boolean
    cancellationDuration?: number
    cancellationRate?: number
    cancellationRateType?: string
    cancellationPolicy?: string
    packageCancellationType?: string
    subscriptionCancellationRate?: number
    subscriptionCancellationRateType?: string
    recipient?: string

    /* product order settings */
    private _orderCancellationDuration?: number // Integer
    orderCancellationRate?: number
    orderCancellationRateType?: string
    orderCancellationPolicy?: string
    orderStatus?: string
    orderConfirmPayment?: string

    monEndHour?: number
    tueEndHour?: number
    wedEndHour?: number
    thuEndHour?: number
    friEndHour?: number
    satEndHour?: number
    sunEndHour?: number
    monStartHour?: number
    tueStartHour?: number
    wedStartHour?: number
    thuStartHour?: number
    friStartHour?: number
    satStartHour?: number
    sunStartHour?: number

    /* GPS location fields */
    longitude?: number
    latitude?: number

    /* payment processing rate fields */
    ratePct?: number
    allowSurcharge?: boolean

    /* sales tax fields */
    taxAddress?: string
    allowSalesTax?: boolean
    alwaysCollectTax?: boolean

    /* google lead settings */
    allowGoogleLead?: boolean
    allowGoogleReview?: boolean
    reviewSchedule?: string
    //todo: implement GoogleLocation
    // private _googleLocationResult?: GoogleLocation

    /* account feature settings */
    featurecap?: number // Integer
    allowClass?: boolean
    allowPackage?: boolean
    allowContract?: boolean
    allowRecord?: boolean
    allowStaffing?: boolean
    allowRecurring?: boolean
    allowReservation?: boolean
    allowServices?: boolean
    allowGratuity?: boolean
    allowEstimate?: boolean
    allowDiscount?: boolean
    allowWidget?: boolean
    allowLink?: boolean
    allowChat?: boolean
    allowLead?: boolean
    allowMarketing?: boolean
    allowTask?: boolean
    allowGift?: boolean
    allowDrip?: boolean
    allowPost?: boolean
    allowRollup?: boolean
    allowRoyalty?: boolean
    allowBilling?: boolean
    allowGroup?: boolean
    allowTemplate?: boolean
    allowReminder?: boolean
    allowSubscription?: boolean
    allowProduct?: boolean
    allowItemize?: boolean
    allowBenchmark?: boolean
    allowPaypal?: boolean
    allowCategory?: boolean
    allowPayment?: boolean
    allowReader?: boolean
    allowMark?: boolean
    allowInvoice?: boolean
    allowBooking?: boolean
    allowTrigger?: boolean
    allowSubaccount?: boolean
    allowAffiliate?: boolean
    allowPrioritySupport?: boolean
    allowBNPL?: boolean
    allowResource?: boolean

    /* misc smart campaign setting(s) */
    renewCredits?: boolean

    // /* misc royalty setting(s) */
    royalty?: number
    marketing?: number
    royaltyTax?: boolean
    royaltyRefund?: boolean
    royaltyGratuity?: boolean
    royaltyItems?: string[]

    hero?: Image
    primaryColor?: string
    secondaryColor?: string
    onboardingState?: string

    /* Paying customer tracking */
    plan?: string

    readonly subscribedDate?: moment.Moment
    readonly requiresUpgrade: boolean = false
    readonly firstPaymentDate?: moment.Moment
    private readonly _anonymous: boolean = false
    readonly inbox?: string

    static currentUser() {
        return PSApplication.me()
    }

    constructor(data?: Partial<UserConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.uri = data.uri ?? undefined
        this.bio = data.bio ?? undefined
        this.url = data.url ?? undefined
        this.username = data.username ?? undefined
        this.key = data.key ?? undefined
        this.role = data.role ?? undefined
        this.source = data.source ?? undefined
        this.login = !!data.login ? data.login : undefined
        this.roleSubtitle = !!data.role_subtitle ? data.role_subtitle : undefined
        this.roleBadge = this.propertyFromIntArg(data.role_badge)
        this.password = data.password ?? undefined
        this.longitude = this.propertyFromNumberArg(data.longitude)
        this.latitude = this.propertyFromNumberArg(data.latitude)
        this.ratePct = this.propertyFromCurrencyArg(data.rate_pct)
        this.plan = data.plan ?? undefined
        this.ship = this.propertyFromCurrencyArg(data.ship)
        this.parcel = data.parcel ?? undefined
        this.carrier = data.carrier ?? undefined
        this.shipType = data.ship_type ?? undefined
        this.shipPricing = data.ship_pricing ?? undefined
        this.pickupDuration = this.propertyFromIntArg(data.pickup_duration)
        this.primaryColor = data.primary_color ?? undefined
        this.secondaryColor = data.secondary_color ?? undefined
        this.signupDate = this.propertyFromTimestampArg(data.signup_date)
        this.lastSyncDate = this.propertyFromTimestampArg(data.last_sync_date)
        this.lastWidgetDate = this.propertyFromTimestampArg(data.last_widget_date)
        this.lockedAlertsDate = this.propertyFromTimestampArg(data.locked_alerts_date)
        this.firstSignupDate = this.propertyFromTimestampArg(data.first_signup_date)
        this.blockPaymentsDate = this.propertyFromTimestampArg(data.block_payments_date)
        this.activatedDate = this.propertyFromTimestampArg(data.activated_date)
        this.verifiedDate = this.propertyFromTimestampArg(data.verified_date)
        this.featurecap = this.propertyFromIntArg(data.featurecap)
        this.allowSalesTax = data.allow_salestax ?? false
        this.alwaysCollectTax = data.always_collect_tax ?? false
        this.allowSurcharge = data.allow_surcharge ?? false
        this.allowClass = data.allow_class ?? false
        this.allowGroup = data.allow_group ?? false
        this.allowTemplate = data.allow_template ?? false
        this.allowPackage = data.allow_package ?? false
        this.allowContract = data.allow_contract ?? false
        this.allowRecord = data.allow_record ?? false
        this.allowStaffing = data.allow_staffing ?? false
        this.allowRecurring = data.allow_recurring ?? false
        this.allowReservation = data.allow_reservation ?? false
        this.allowGratuity = data.allow_gratuity ?? false
        this.allowServices = data.allow_services ?? false
        this.allowEstimate = data.allow_estimate ?? false
        this.allowDiscount = data.allow_discount ?? false
        this.allowWidget = data.allow_widget ?? false
        this.allowLink = data.allow_link ?? false
        this.allowMarketing = data.allow_marketing ?? false
        this.allowChat = data.allow_chat ?? false
        this.allowMark = data.allow_mark ?? false
        this.allowLead = data.allow_lead ?? false
        this.allowTask = data.allow_task ?? false
        this.allowGift = data.allow_gift ?? false
        this.allowDrip = data.allow_drip ?? false
        this.allowPost = data.allow_post ?? false
        this.allowRollup = data.allow_rollup ?? false
        this.allowRoyalty = data.allow_royalty ?? false
        this.allowBilling = data.allow_billing ?? false
        this.allowReminder = data.allow_reminder ?? false
        this.allowSubscription = data.allow_subscription ?? false
        this.allowProduct = data.allow_product ?? false
        this.allowItemize = data.allow_itemize ?? false
        this.allowBenchmark = data.allow_benchmark ?? false
        this.allowReader = data.allow_reader ?? false
        this.allowPayment = data.allow_payment ?? false
        this.allowBooking = data.allow_booking ?? false
        this.allowTrigger = data.allow_trigger ?? false
        this.allowSubaccount = data.allow_subaccount ?? false
        this.allowInvoice = data.allow_invoice ?? false
        this.allowPaypal = data.allow_paypal ?? false
        this.allowCategory = data.allow_category ?? false
        this.allowAffiliate = data.allow_affiliate ?? false
        this.allowPrioritySupport = data.allow_priority_support ?? false
        this.allowBNPL = data.allow_bnpl ?? false
        this.allowResource = data.allow_resource ?? false
        this.renewCredits = data.renew_credits ?? false
        this.silenceTransactions = data.silence_transactions ?? undefined
        this.doubleNotify = data.double_notify ?? false
        this.travel = data.travel ?? undefined
        this.channel = data.channel ?? undefined
        this.reminder = data.reminder ?? undefined
        this.invoiceReminder = data.invoice_reminder ?? undefined
        this.bookingStatus = data.booking_status ?? undefined
        this.confirmPayment = data.confirm_payment ?? undefined
        this.orderStatus = data.order_status ?? undefined
        this.orderConfirmPayment = data.order_confirm_payment ?? undefined
        this.bookingFlexStart = data.booking_flex_start ?? undefined
        this.bookingAssignment = data.booking_assignment ?? undefined
        this.instantPayout = data.instant_payout ?? false
        this.firstMPCDate = this.propertyFromDateArg(data.first_mpc_date)
        this.sms = data.sms ?? undefined
        this.main = data.main ?? undefined
        this.size = data.size ?? undefined
        this.goal = data.goal ?? undefined
        this.team = data.team ?? false
        this.income = data.income ?? undefined
        this.volume = data.volume ?? undefined
        this.marketingReferrer = data.marketing_referrer ?? undefined
        this.instagram = data.instagram ?? undefined
        this.type = data.type ?? undefined
        this.recipient = data.recipient ?? undefined
        this.taxAddress = data.tax_address ?? undefined
        this.timezone = data.timezone ?? undefined
        this.availableRange = data.available_range ?? undefined
        this.gratuity = this.propertyFromCurrencyArg(data.gratuity)
        this.requestGratuity = data.request_gratuity ?? undefined
        this.minimumGratuity = this.propertyFromCurrencyArg(data.minimum_gratuity)
        this.gratuityOptions = data.gratuity_options ?? undefined
        this.schedule = this.propertyFromIntArg(data.schedule)
        this.capacity = this.propertyFromIntArg(data.capacity)
        this.reservationCapacity = this.propertyFromIntArg(data.reservation_capacity)
        this.orderPolicy = data.order_policy ?? undefined
        this.invoicePolicy = data.invoice_policy ?? undefined
        this.bookingPolicy = data.booking_policy ?? undefined
        this.cancellationPolicy = data.cancellation_policy ?? undefined
        this.orderCancellationPolicy = data.order_cancellation_policy ?? undefined
        this.cancellationDuration = this.propertyFromIntArg(data.cancellation_duration)
        this.receiptPolicy = data.receipt_policy ?? undefined
        this.estimatePolicy = data.estimate_policy ?? undefined
        this.subscriptionCancellationRate = this.propertyFromCurrencyArg(
            data.subscription_cancellation_rate
        )
        this.subscriptionCancellationRateType =
            data.subscription_cancellation_rate_type ?? undefined
        this.cancellationRate = this.propertyFromCurrencyArg(data.cancellation_rate)
        this.cancellationRateType = data.cancellation_rate_type ?? undefined
        this.cancellationDuration = this.propertyFromIntArg(data.cancellation_duration)
        this.orderCancellationRate = this.propertyFromCurrencyArg(data.order_cancellation_rate)
        this.orderCancellationRateType = data.order_cancellation_rate_type ?? undefined
        this.orderCancellationDuration = this.propertyFromIntArg(data.order_cancellation_duration)
        this.packageCancellationType = data.package_cancellation_type ?? undefined
        this.vendorLocation = data.location ?? false
        this.monStartHour = this.propertyFromNumberArg(data.mon_start_hour)
        this.tueStartHour = this.propertyFromNumberArg(data.tue_start_hour)
        this.wedStartHour = this.propertyFromNumberArg(data.wed_start_hour)
        this.thuStartHour = this.propertyFromNumberArg(data.thu_start_hour)
        this.friStartHour = this.propertyFromNumberArg(data.fri_start_hour)
        this.satStartHour = this.propertyFromNumberArg(data.sat_start_hour)
        this.sunStartHour = this.propertyFromNumberArg(data.sun_start_hour)
        this.monEndHour = this.propertyFromNumberArg(data.mon_end_hour)
        this.tueEndHour = this.propertyFromNumberArg(data.tue_end_hour)
        this.wedEndHour = this.propertyFromNumberArg(data.wed_end_hour)
        this.thuEndHour = this.propertyFromNumberArg(data.thu_end_hour)
        this.friEndHour = this.propertyFromNumberArg(data.fri_end_hour)
        this.satEndHour = this.propertyFromNumberArg(data.sat_end_hour)
        this.sunEndHour = this.propertyFromNumberArg(data.sun_end_hour)
        this.onboardingState = data.onboarding_state ?? undefined
        this.allowGoogleLead = data.allow_google_lead ?? false
        this.allowGoogleReview = data.allow_google_review ?? false
        this.reviewSchedule = data.review_schedule ?? undefined
        this.schedule = this.propertyFromIntArg(data.schedule)
        // todo: implement GoogleLocation
        // const location = dataRecordDataMap.containsKey('google_location_result')
        //     ? dataRecordDataMap.valueForDictionaryKey('google_location_result')
        //     : undefined
        // this._googleLocationResult = location === undefined || Object.keys(location).length === 0 ? undefined : new GoogleLocation(location)
        this.hero = this.propertyFromImageArg(data.hero, data.hero_uri)

        this.subscribedDate = this.propertyFromTimestampArg(data.subscribed_date)
        this.requiresUpgrade = data.requires_upgrade ?? false
        this.firstPaymentDate = this.propertyFromTimestampArg(data.first_payment_date)
        this._anonymous = data.anonymous ?? false
        this.inbox = data.inbox ?? this.id
    }

    clone(overrides?: Partial<UserConstructorArgs>): User {
        return new User({
            ...this.serializeForClone(),
            location: this.vendorLocation,
            allow_salestax: this.allowSalesTax,
            ...overrides,
        })
    }

    static initWithId(id: string): User {
        const user = CacheFactory.getInstance().getRecord(User, id) ?? new User({ id })
        return user
    }

    get canLoginViaAccountSwitcher(): boolean {
        return (
            this.type === 'solo' ||
            this.type === 'company' ||
            this.type === 'partner' ||
            (this.type === 'payee' && this.role === 'admin')
        )
    }

    static reviewScheduleStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, {
                id: '',
                label: 'None',
                subtitle: 'Never request reviews over text after appointments',
            }),
            new Option(undefined, {
                id: 'monthly',
                label: 'Only once per month',
                subtitle:
                    'Request reviews over text after an appointment a maximum of once per month',
            }),
            new Option(undefined, {
                id: 'quarterly',
                label: 'Only once every 3 month',
                subtitle:
                    'Request reviews over text after an appointment a maximum of once every three months',
            }),
            new Option(undefined, {
                id: 'biannually',
                label: 'Only once every 6 months',
                subtitle:
                    'Request reviews over text after an appointment a maximum of once every six months',
            }),
            new Option(undefined, {
                id: 'annually',
                label: 'Only once every year',
                subtitle:
                    'Request reviews over text after an appointment a maximum of once every year',
            }),
            new Option(undefined, {
                id: 'always',
                label: 'Always',
                subtitle: 'Request reviews over text after every appointment',
            }),
        ]
        return strings
    }

    static incomeStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: 'zero', label: '$0' }),
            new Option(undefined, { id: 'micro', label: '$0 - $999' }),
            new Option(undefined, { id: 'small', label: '$1000 - $1999' }),
            new Option(undefined, { id: 'medium', label: '$2000 - $9999' }),
            new Option(undefined, { id: 'large', label: '$10000+' }),
        ]
        return strings
    }

    static volumeStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: 'zero', label: '0 clients' }),
            new Option(undefined, { id: 'micro', label: '1 - 5 clients' }),
            new Option(undefined, { id: 'small', label: '6 - 19 clients' }),
            new Option(undefined, { id: 'medium', label: '20 - 49 clients' }),
            new Option(undefined, { id: 'large', label: '50 - 99 clients' }),
            new Option(undefined, { id: 'enterprise', label: '100+ clients' }),
        ]
        return strings
    }

    static roleStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: 'admin', label: 'Administrator' }),
            new Option(undefined, { id: 'employee', label: 'Staff' }),
            new Option(undefined, { id: 'contractor', label: 'Limited' }),
        ]
        return strings
    }

    static allRoleStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: 'owner', label: 'Owner' }),
            new Option(undefined, { id: 'admin', label: 'Administrator' }),
            new Option(undefined, { id: 'employee', label: 'Staff' }),
            new Option(undefined, { id: 'contractor', label: 'Limited' }),
        ]
        return strings
    }

    static requireCardStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: 'never', label: "Don't require" }),
            new Option(undefined, { id: 'ifPriced', label: 'Only if priced' }),
            new Option(undefined, { id: 'always', label: 'Always' }),
        ]
        return strings
    }

    static confirmStatusStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: 'confirmed', label: 'Already confirmed' }),
            new Option(undefined, { id: 'confirmOnly', label: 'Requires confirmation' }),
            new Option(undefined, { id: 'confirmPayment', label: 'Requires credit card' }),
        ]
        return strings
    }

    static silenceTransactionStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: '', label: "Don't silence" }),
            new Option(undefined, { id: 'silence', label: 'Silence only' }),
            new Option(undefined, { id: 'mark', label: 'Silence & mark read' }),
        ]
        return strings
    }

    static cancellationDurationStrings(): Option[] {
        return [
            new Option(undefined, { id: '1', label: '1 hour' }),
            new Option(undefined, { id: '2', label: '2 hours' }),
            new Option(undefined, { id: '6', label: '6 hours' }),
            new Option(undefined, { id: '12', label: '12 hours' }),
            new Option(undefined, { id: '24', label: '24 hours' }),
            new Option(undefined, { id: '48', label: '48 hours' }),
            new Option(undefined, { id: '72', label: '72 hours' }),
            new Option(undefined, { id: '168', label: 'One week' }),
            new Option(undefined, { id: '336', label: 'Two weeks' }),
            new Option(undefined, { id: '672', label: 'Four weeks' }),
        ]
    }

    static orderCancellationDurationStrings(): Option[] {
        return [
            new Option(undefined, { id: '1', label: '1 hour' }),
            new Option(undefined, { id: '2', label: '2 hours' }),
            new Option(undefined, { id: '4', label: '4 hours' }),
            new Option(undefined, { id: '6', label: '6 hours' }),
            new Option(undefined, { id: '12', label: '12 hours' }),
            new Option(undefined, { id: '24', label: '24 hours' }),
        ]
    }

    static packageCancellationTypeStrings(): Option[] {
        return [
            new Option(undefined, { id: 'charge', label: 'Charge fee' }),
            new Option(undefined, { id: 'session', label: 'Lose session' }),
            new Option(undefined, { id: 'both', label: 'Both' }),
        ]
    }

    static assignmentStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, {
                id: 'auto',
                label: 'Automatically',
                subtitle: 'PocketSuite will auto-assign team members to unstaffed jobs',
            }),
            new Option(undefined, {
                id: 'manual',
                label: 'Manually',
                subtitle: 'Manually assign team members to unstaffed jobs',
            }),
            new Option(undefined, {
                id: 'client',
                label: 'Let clients choose',
                subtitle: 'Clients are required to select a team member when booking',
            }),
        ]
        return strings
    }

    get shipTypeString(): string | undefined {
        const shipType = this.shipType
        const l = Order.shipTypeStrings()
        for (let i = 0; i < l.length; i++) {
            const opt = l[i]
            if (opt.id === shipType) return opt.label
        }
        return undefined
    }

    get allowField(): boolean | undefined {
        return this.allowWidget || this.allowLead || this.allowSubaccount || this.allowProduct
    }

    get weekDays(): number[] {
        let l: number[] = []
        let schedule = this.schedule ?? 0
        if (schedule !== 0) {
            let idx = 1
            for (let i = 0; i < 7; i++) if ((schedule & (idx << i)) !== 0) l.push(idx << i)
        }
        return l
    }

    get description(): string {
        let string = ''
        let city = this.city
        let state = this.state
        if (state && city) string = city + ', ' + state
        else if (this.phone) string = formatPhoneNumber(this.phone)
        else if (this.email) string = this.email
        return string
    }

    set weekDays(weekDays: number[]) {
        let schedule = 0
        for (let i of weekDays) schedule |= i
        this.schedule = schedule
    }

    get cancellationRateString(): string | undefined {
        let cancellationRate = this.cancellationRate
        if (cancellationRate) {
            if (this.cancellationRateType === 'percent') return `${formatFloat(cancellationRate)}%`
            return currencyFormat(cancellationRate)
        }
        return undefined
    }

    get orderCancellationRateString(): string | undefined {
        let orderCancellationRate = this.orderCancellationRate
        if (orderCancellationRate) {
            if (this.orderCancellationRateType === 'percent')
                return `${formatFloat(orderCancellationRate)}%`
            return currencyFormat(orderCancellationRate)
        }
        return undefined
    }

    get subscriptionCancellationRateString(): string | undefined {
        let subscriptionCancellationRate = this.subscriptionCancellationRate
        if (subscriptionCancellationRate) {
            if (this.subscriptionCancellationRateType === 'percent')
                return `${formatFloat(subscriptionCancellationRate)}%`
            return currencyFormat(subscriptionCancellationRate)
        }
        return undefined
    }

    get cancellationDurationString(): string | undefined {
        if (!this.cancellationDuration) return undefined

        let selectedDuration = Option.findOption(
            this.cancellationDuration.toString(),
            User.cancellationDurationStrings()
        )
        if (selectedDuration) return selectedDuration?.label
        return undefined
    }

    get orderCancellationDurationString(): string | undefined {
        if (!this.orderCancellationDuration) return undefined

        let selectedDuration = Option.findOption(
            this.cancellationDuration?.toString(),
            User.cancellationDurationStrings()
        )
        if (selectedDuration) return selectedDuration.label
        return undefined
    }

    get packageCancellationTypeString(): string | undefined {
        let selectedPackageCancellationTypeString = Option.findOption(
            this.packageCancellationType,
            User.packageCancellationTypeStrings()
        )
        if (selectedPackageCancellationTypeString) {
            return selectedPackageCancellationTypeString.label
        }
        return undefined
    }

    get latestDateForClientsToBook(): moment.Moment | undefined {
        if (!this.availableRange || this.availableRange.length < 2) return undefined

        let now = moment()

        if (this.availableRange === 'tw') {
            return now.startOf('isoWeek').add(1, 'week')
        }
        else if (this.availableRange === 'tm') {
            return now.startOf('month').add(1, 'month')
        }
        else {
            let unit = this.availableRange.charAt(this.availableRange.length - 1)
            let amountStr = this.availableRange.substring(0, this.availableRange.length - 1)
            if (unit === 'd') {
                return now.add(parseInt(amountStr), 'days')
            }
            else if (unit === 'w') {
                return now.add(parseInt(amountStr) * 7, 'days')
            }
            else if (unit === 'm') {
                return now.add(parseInt(amountStr), 'months')
            }
        }

        return undefined
    }

    get sp(): boolean {
        return this.type !== undefined && ['company', 'solo', 'payee'].includes(this.type)
    }

    get client(): boolean {
        return this.type === 'client'
    }

    get payee(): boolean {
        return this.type === 'payee'
    }

    get solo(): boolean {
        return this.type === 'solo'
    }

    get company(): boolean {
        return this.type === 'company'
    }

    get partner(): boolean {
        return this.type === 'partner'
    }

    get anonymous(): boolean {
        return this.id ? this._anonymous : false
    }

    get owner(): User {
        return this.payee && this.employer ? this.employer : this
    }

    get employee(): User | undefined {
        return this.payee &&
            this.employer &&
            (this.role === 'employee' || this.role === 'contractor')
            ? this
            : undefined
    }

    get perm(): number {
        if (this.solo || this.company) return User.PERM_ROLE_OWNER
        if (this.client) return User.PERM_ROLE_CLIENT
        if (this.partner) return User.PERM_ROLE_PARTNER
        if (this.role === 'admin') return User.PERM_ROLE_ADMIN
        if (this.role === 'employee') return User.PERM_ROLE_EMPLOYEE
        if (this.role === 'contractor') return User.PERM_ROLE_CONTRACTOR
        return User.PERM_ROLE_CLIENT
    }

    get person(): User | undefined {
        return undefined
    }

    get date(): moment.Moment | undefined {
        return undefined
    }

    get duration(): number | undefined {
        return undefined
    }

    get title(): string | undefined {
        return this.name
    }

    get shortTitle(): string | undefined {
        return this.title
    }

    get coordinate(): [number, number] | undefined {
        if (this.latitude !== undefined && this.longitude !== undefined)
            return [this.latitude, this.longitude]
        return undefined
    }

    toString(): string {
        let string: string | undefined = undefined
        const city = this.city
        const state = this.state
        if (state && city) string = `${city}, ${state}`
        else if (this.phone) string = formatPhoneNumber(this.phone)
        else if (this.email) string = this.email
        return string ?? ''
    }

    set pickupDuration(pickupDuration: number | undefined) {
        if (pickupDuration !== undefined)
            this._pickupDuration = parseInt(Number(pickupDuration).toString())
        else this._pickupDuration = undefined
    }

    set capacity(capacity: number | undefined) {
        if (capacity !== undefined) this._capacity = parseInt(Number(capacity).toString())
        else this._capacity = undefined
    }

    get capacity() {
        return this._capacity
    }

    set reservationCapacity(reservationCapacity: number | undefined) {
        if (reservationCapacity !== undefined)
            this._reservationCapacity = parseInt(Number(reservationCapacity).toString())
        else this._reservationCapacity = undefined
    }

    static bookingCancellationString(vendor: User, booking: Booking): string | undefined {
        if (vendor.cancellationDuration && vendor.cancellationRate) {
            // const cancellationString = "If you cancel after %s you will be charged a %s cancellation fee";
            const cancellationCost =
                vendor.cancellationRateType === 'rate'
                    ? currencyFormat(vendor.cancellationRate)
                    : currencyFormat((vendor.cancellationRate * (booking.total || 0)) / 100)
            const dateDisplay = moment(booking.date)
                .subtract(vendor.cancellationDuration, 'hours')
                .format("MMM D, YYYY 'at' h:mm a")
            // return util.format(cancellationString, dateDisplay, cancellationCost);
            return `If you cancel after ${dateDisplay} you will be charged a ${cancellationCost} cancellation fee`
        }
        return undefined
    }

    static orderCancellationString(vendor: User, order: Order): string | undefined {
        if (vendor.cancellationDuration && vendor.orderCancellationRate) {
            const cancellationCost =
                vendor.orderCancellationRateType === 'rate'
                    ? currencyFormat(vendor.orderCancellationRate)
                    : currencyFormat((vendor.orderCancellationRate * (order.total || 0)) / 100)
            const createdDate = order.createdDate || moment()
            const dateDisplay = moment(createdDate)
                .subtract(vendor.orderCancellationDuration, 'hours')
                .format("MMM D, YYYY 'at' h:mm a")
            return `If you cancel after ${dateDisplay} you will be charged a ${cancellationCost} cancellation fee`
        }
        return undefined
    }

    set orderCancellationDuration(orderCancellationDuration: number | undefined) {
        if (orderCancellationDuration !== undefined)
            this._orderCancellationDuration = parseInt(Number(orderCancellationDuration).toString())
        else this._orderCancellationDuration = undefined
    }

    allowFeatureByID(featureID: 'widget' | 'chat' | 'lead' | 'paypal' | 'booking') {
        switch (featureID) {
            case 'widget':
                this.allowWidget = true
                break
            case 'chat':
                this.allowChat = true
                break
            case 'lead':
                this.allowLead = true
                break
            case 'paypal':
                this.allowPaypal = true
                break
            case 'booking':
                this.allowBooking = true
                break
        }
    }

    // `day` should be one of 'mon', 'tue', 'wed', etc. We're not typing it that way
    // to allow for more flexible access in other parts of the codebase.
    getStartHourOnDay(day: string): number | undefined {
        switch (day) {
            case 'mon': {
                return this.monStartHour
            }
            case 'tue': {
                return this.tueStartHour
            }
            case 'wed': {
                return this.wedStartHour
            }
            case 'thu': {
                return this.thuStartHour
            }
            case 'fri': {
                return this.friStartHour
            }
            case 'sat': {
                return this.satStartHour
            }
            case 'sun': {
                return this.sunStartHour
            }
            default:
                return undefined
        }
    }
    // `day` should be one of 'mon', 'tue', 'wed', etc. We're not typing it that way
    // to allow for more flexible access in other parts of the codebase.
    getEndHourOnDay(day: string): number | undefined {
        switch (day) {
            case 'mon': {
                return this.monEndHour
            }
            case 'tue': {
                return this.tueEndHour
            }
            case 'wed': {
                return this.wedEndHour
            }
            case 'thu': {
                return this.thuEndHour
            }
            case 'fri': {
                return this.friEndHour
            }
            case 'sat': {
                return this.satEndHour
            }
            case 'sun': {
                return this.sunEndHour
            }
            default:
                return undefined
        }
    }

    startHourOnDate(date: moment.Moment): number | undefined {
        switch (date.day()) {
            case 0: // Sunday
                return this.sunStartHour
            case 1: // Monday
                return this.monStartHour
            case 2: // Tuesday
                return this.tueStartHour
            case 3: // Wednesday
                return this.wedStartHour
            case 4: // Thursday
                return this.thuStartHour
            case 5: // Friday
                return this.friStartHour
            case 6: // Saturday
                return this.satStartHour
            default:
                return undefined
        }
    }

    endHourOnDate(date: moment.Moment): number | undefined {
        switch (date.day()) {
            case 0: // Sunday
                return this.sunEndHour
            case 1: // Monday
                return this.monEndHour
            case 2: // Tuesday
                return this.tueEndHour
            case 3: // Wednesday
                return this.wedEndHour
            case 4: // Thursday
                return this.thuEndHour
            case 5: // Friday
                return this.friEndHour
            case 6: // Saturday
                return this.satEndHour
            default:
                return undefined
        }
    }

    setDayStartHour(
        day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
        value: number | undefined
    ) {
        switch (day) {
            case 'mon': {
                this.monStartHour = value
                break
            }
            case 'tue': {
                this.tueStartHour = value
                break
            }
            case 'wed': {
                this.wedStartHour = value
                break
            }
            case 'thu': {
                this.thuStartHour = value
                break
            }
            case 'fri': {
                this.friStartHour = value
                break
            }
            case 'sat': {
                this.satStartHour = value
                break
            }
            case 'sun': {
                this.sunStartHour = value
                break
            }
        }
    }

    setDayEndHour(
        day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
        value: number | undefined
    ) {
        switch (day) {
            case 'mon': {
                this.monEndHour = value
                break
            }
            case 'tue': {
                this.tueEndHour = value
                break
            }
            case 'wed': {
                this.wedEndHour = value
                break
            }
            case 'thu': {
                this.thuEndHour = value
                break
            }
            case 'fri': {
                this.friEndHour = value
                break
            }
            case 'sat': {
                this.satEndHour = value
                break
            }
            case 'sun': {
                this.sunEndHour = value
                break
            }
        }
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.employer) this.employer = BaseRecord.fromSerializer(data.employer, User)
        if (data.marketingChannel)
            this.marketingChannel = BaseRecord.fromSerializer(data.marketingChannel, Channel)
        if (data.taxcode) this.taxcode = BaseRecord.fromSerializer(data.taxcode, Taxcode)
        if (data.leadForm) this.leadForm = BaseRecord.fromSerializer(data.leadForm, Type)
        if (data.orderForm) this.orderForm = BaseRecord.fromSerializer(data.orderForm, Type)
        if (data.bookingForm) this.bookingForm = BaseRecord.fromSerializer(data.bookingForm, Type)
        if (data.leadContract)
            this.leadContract = BaseRecord.fromSerializer(data.leadContract, Contract)
        if (data.orderContract)
            this.orderContract = BaseRecord.fromSerializer(data.orderContract, Contract)
        if (data.bookingContract)
            this.bookingContract = BaseRecord.fromSerializer(data.bookingContract, Contract)
        if (data.hero) this.hero = BaseRecord.fromSerializer(data.hero, Image)
        if (data.signupDate) this.signupDate = moment(data.signupDate)
        if (data.firstSignupDate) this.firstSignupDate = moment(data.firstSignupDate)
        if (data.blockPaymentsDate) this.blockPaymentsDate = moment(data.blockPaymentsDate)
        if (data.lastSyncDate) this.lastSyncDate = moment(data.lastSyncDate)
        if (data.lastWidgetDate) this.lastWidgetDate = moment(data.lastWidgetDate)
        if (data.lockedAlertsDate) this.lockedAlertsDate = moment(data.lockedAlertsDate)
        if (data.firstMPCDate) this.firstMPCDate = moment(data.firstMPCDate)
        if (data.activatedDate) this.activatedDate = moment(data.activatedDate)
        if (data.verifiedDate) this.verifiedDate = moment(data.verifiedDate)
    }

    static fk: FKType = mergeFk(Person.fk, {
        users: ['employer'],
        channels: ['marketingChannel'],
        taxcodes: ['taxcode'],
        types: ['leadForm', 'orderForm', 'bookingForm'],
        contracts: ['leadContract', 'orderContract', 'bookingContract'],
    })

    static dataType(): DataType {
        return 'users'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        if (this.industry) dict['industry'] = this.industry.id
        if (this.bio) dict['bio'] = this.bio
        if (this.size) dict['size'] = this.size
        dict['team'] = this.team
        if (this.goal) dict['goal'] = this.goal
        if (this.income) dict['income'] = this.income
        if (this.volume) dict['volume'] = this.volume
        if (this.marketingChannel) dict['marketing_channel'] = this.marketingChannel.id
        if (this.marketingReferrer) dict['marketing_referrer'] = this.marketingReferrer
        if (this.instagram) dict['instagram'] = this.instagram
        if (this.type) dict['type'] = this.type
        if (this.role && this.type === 'payee') dict['role'] = this.role
        if (this.url) dict['url'] = this.url
        if (this.username) dict['username'] = this.username
        if (this.timezone) dict['timezone'] = this.timezone
        dict['available_range'] = this.availableRange ?? ''
        if (this.schedule) dict['schedule'] = this.schedule
        if (this.capacity) dict['capacity'] = this.capacity
        if (this.reservationCapacity) dict['reservation_capacity'] = this.reservationCapacity

        dict['royalty'] = this.royalty ?? ''
        dict['marketing'] = this.marketing ?? ''
        dict['royalty_tax'] = this.royaltyTax
        dict['royalty_refund'] = this.royaltyRefund
        dict['royalty_gratuity'] = this.royaltyGratuity
        dict['royalty_items'] = this.royaltyItems?.length ? this.royaltyItems : ''
        dict['gratuity'] = this.gratuity ?? ''
        dict['employer'] = this.employer?.id ?? ''
        dict['ship'] = this.ship ?? ''
        dict['parcel'] = this.parcel ?? ''
        dict['carrier'] = this.carrier ?? ''
        dict['ship_type'] = this.shipType ?? ''
        dict['ship_pricing'] = this.shipPricing ?? ''
        dict['pickup_duration'] = this.pickupDuration ?? ''
        dict['request_gratuity'] = this.requestGratuity ?? ''
        dict['minimum_gratuity'] = this.minimumGratuity
        dict['order_policy'] = this.orderPolicy ?? ''
        dict['invoice_policy'] = this.invoicePolicy ?? ''
        dict['booking_policy'] = this.bookingPolicy ?? ''
        dict['cancellation_policy'] = this.cancellationPolicy ?? ''
        dict['order_cancellation_policy'] = this.orderCancellationPolicy ?? ''
        dict['receipt_policy'] = this.receiptPolicy ?? ''
        dict['estimate_policy'] = this.estimatePolicy ?? ''
        dict['cancellation_rate'] = this.cancellationRate ?? ''
        dict['cancellation_duration'] = this.cancellationDuration ?? ''
        dict['cancellation_rate_type'] = this.cancellationRateType ?? ''
        dict['order_cancellation_rate'] = this.orderCancellationRate ?? ''
        dict['order_cancellation_duration'] = this.orderCancellationDuration ?? ''
        dict['order_cancellation_rate_type'] = this.orderCancellationRateType ?? ''
        dict['package_cancellation_type'] = this.packageCancellationType ?? ''
        dict['subscription_cancellation_rate'] = this.subscriptionCancellationRate ?? ''
        dict['subscription_cancellation_rate_type'] = this.subscriptionCancellationRateType ?? ''
        dict['main'] = this.main ?? ''
        if (this.featurecap) dict['featurecap'] = this.featurecap
        dict['allow_surcharge'] = this.allowSurcharge
        dict['allow_salestax'] = this.allowSalesTax
        dict['allow_class'] = this.allowClass
        dict['allow_group'] = this.allowGroup
        dict['allow_template'] = this.allowTemplate
        dict['allow_package'] = this.allowPackage
        dict['allow_contract'] = this.allowContract
        dict['allow_record'] = this.allowRecord
        dict['allow_staffing'] = this.allowStaffing
        dict['allow_recurring'] = this.allowRecurring
        dict['allow_reservation'] = this.allowReservation
        dict['allow_services'] = this.allowServices
        dict['allow_gratuity'] = this.allowGratuity
        dict['allow_estimate'] = this.allowEstimate
        dict['allow_discount'] = this.allowDiscount
        dict['allow_widget'] = this.allowWidget
        dict['allow_link'] = this.allowLink
        dict['allow_marketing'] = this.allowMarketing
        dict['allow_chat'] = this.allowChat
        dict['allow_lead'] = this.allowLead
        dict['allow_task'] = this.allowTask
        dict['allow_gift'] = this.allowGift
        dict['allow_drip'] = this.allowDrip
        dict['allow_post'] = this.allowPost
        dict['allow_rollup'] = this.allowRollup
        dict['allow_royalty'] = this.allowRoyalty
        dict['allow_billing'] = this.allowBilling
        dict['allow_reminder'] = this.allowReminder
        dict['allow_subscription'] = this.allowSubscription
        dict['allow_product'] = this.allowProduct
        dict['allow_itemize'] = this.allowItemize
        dict['allow_invoice'] = this.allowInvoice
        dict['allow_benchmark'] = this.allowBenchmark
        dict['allow_payment'] = this.allowPayment
        dict['allow_reader'] = this.allowReader
        dict['allow_mark'] = this.allowMark
        dict['allow_booking'] = this.allowBooking
        dict['allow_trigger'] = this.allowTrigger
        dict['allow_subaccount'] = this.allowSubaccount
        dict['allow_paypal'] = this.allowPaypal
        dict['allow_category'] = this.allowCategory
        dict['allow_google_lead'] = this.allowGoogleLead
        dict['allow_google_review'] = this.allowGoogleReview
        dict['allow_bnpl'] = this.allowBNPL
        dict['allow_resource'] = this.allowResource
        dict['review_schedule'] = this.reviewSchedule
        dict['travel'] = this.travel ?? ''
        dict['channel'] = this.channel ?? ''
        dict['reminder'] = this.reminder ?? ''
        dict['invoice_reminder'] = this.invoiceReminder ?? ''
        dict['booking_status'] = this.bookingStatus ?? ''
        dict['confirm_payment'] = this.confirmPayment ?? ''
        dict['order_status'] = this.orderStatus ?? ''
        dict['order_confirm_payment'] = this.orderConfirmPayment ?? ''
        dict['booking_flex_start'] = this.bookingFlexStart ?? ''
        dict['booking_assignment'] = this.bookingAssignment ?? ''
        dict['instant_payout'] = this.instantPayout
        dict['silence_transactions'] = this.silenceTransactions ?? ''
        dict['double_notify'] = this.doubleNotify
        dict['lead_form'] = this.leadForm?.id ?? ''
        dict['order_form'] = this.orderForm?.id ?? ''
        dict['booking_form'] = this.bookingForm?.id ?? ''
        dict['lead_contract'] = this.leadContract?.id ?? ''
        dict['order_contract'] = this.orderContract?.id ?? ''
        dict['booking_contract'] = this.bookingContract?.id ?? ''
        if (this.lockedAlertsDate)
            dict['locked_alerts_date'] = formatTimeStamp(this.lockedAlertsDate)
        dict['location'] = this.vendorLocation
        dict['mon_start_hour'] = this.monStartHour ?? ''
        dict['mon_end_hour'] = this.monEndHour ?? ''
        dict['tue_start_hour'] = this.tueStartHour ?? ''
        dict['tue_end_hour'] = this.tueEndHour ?? ''
        dict['wed_start_hour'] = this.wedStartHour ?? ''
        dict['wed_end_hour'] = this.wedEndHour ?? ''
        dict['thu_start_hour'] = this.thuStartHour ?? ''
        dict['thu_end_hour'] = this.thuEndHour ?? ''
        dict['fri_start_hour'] = this.friStartHour ?? ''
        dict['fri_end_hour'] = this.friEndHour ?? ''
        dict['sat_start_hour'] = this.satStartHour ?? ''
        dict['sat_end_hour'] = this.satEndHour ?? ''
        dict['sun_start_hour'] = this.sunStartHour ?? ''
        dict['sun_end_hour'] = this.sunEndHour ?? ''
        dict['primary_color'] = this.primaryColor ?? ''
        dict['secondary_color'] = this.secondaryColor ?? ''
        dict['login'] = this.login ?? ''
        return dict
    }

    get marketingChannel(): Channel | undefined {
        return this.getForeignRecord('marketing_channel', Channel)
    }
    set marketingChannel(marketingChannel: Channel | undefined) {
        this.setForeignRecord('marketing_channel', marketingChannel)
    }
    get taxcode(): Taxcode | undefined {
        return this.getForeignRecord('taxcode', Taxcode)
    }
    set taxcode(taxcode: Taxcode | undefined) {
        this.setForeignRecord('taxcode', taxcode)
    }
    get leadContract(): Contract | undefined {
        return this.getForeignRecord('lead_contract', Contract)
    }
    set leadContract(leadContract: Contract | undefined) {
        this.setForeignRecord('lead_contract', leadContract)
    }
    get orderContract(): Contract | undefined {
        return this.getForeignRecord('order_contract', Contract)
    }
    set orderContract(orderContract: Contract | undefined) {
        this.setForeignRecord('order_contract', orderContract)
    }
    get bookingContract(): Contract | undefined {
        return this.getForeignRecord('booking_contract', Contract)
    }
    set bookingContract(bookingContract: Contract | undefined) {
        this.setForeignRecord('booking_contract', bookingContract)
    }
    get leadForm(): Type | undefined {
        return this.getForeignRecord('lead_form', Type)
    }
    set leadForm(leadForm: Type | undefined) {
        this.setForeignRecord('lead_form', leadForm)
    }
    get orderForm(): Type | undefined {
        return this.getForeignRecord('order_form', Type)
    }
    set orderForm(orderForm: Type | undefined) {
        this.setForeignRecord('order_form', orderForm)
    }
    get bookingForm(): Type | undefined {
        return this.getForeignRecord('booking_form', Type)
    }
    set bookingForm(bookingForm: Type | undefined) {
        this.setForeignRecord('booking_form', bookingForm)
    }
    get employer(): User | undefined {
        return this.getForeignRecord('employer', User)
    }
    set employer(employer: User | undefined) {
        this.setForeignRecord('employer', employer)
    }
    get referer(): User | undefined {
        return this.getForeignRecord('referer', User)
    }
    set referer(referer: User | undefined) {
        this.setForeignRecord('referer', referer)
    }
    get edition(): Edition | undefined {
        return this.getForeignRecord('edition', Edition)
    }
    set edition(edition: Edition | undefined) {
        this.setForeignRecord('edition', edition)
    }
    get allowedEdition(): Edition | undefined {
        return this.getForeignRecord('allowed_edition', Edition)
    }
    set allowedEdition(allowedEdition: Edition | undefined) {
        this.setForeignRecord('allowed_edition', allowedEdition)
    }
}
